import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, IconButton, Divider, Button } from "@mui/material";
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { atom_listModeState } from '@app/shared/state/list-mode.atom';
import { atom_isDesktop, atom_tourState } from '@app/shared/state/tour.atom';
import { atom_selectedPlace } from '@app/shared/state/saved-places.atom';
import { atom_allBookmarksState } from '@app/shared/state/filter.atom';
import { atom_selectedUserPlace, atom_userAddedPlaces, PlaceDetails } from '@app/shared/state/user-added-stops.atom';
import { atom_dropPinMode } from '@app/shared/state/drop-pin-mode.atom';
import { atom_navigationHelperState } from '@app/shared/state/show-content-modal.atom';
import { atom_mapRef } from '@app/shared/state/map-ref.atom';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { useTranslation } from 'react-i18next';
import MobilePlaceSearch from './MobilePlaceSearch';
import MobileDropAPin from './MobileDropAPin';
import { Z_INDEX } from '@app/shared/styles/zIndex';
import { useResizablePanel } from '@app/shared/hooks/useResizablePanel';
import DirectionIcon from '@app/new-app/direction.svg';

interface MobileAddPlaceContentProps {
  contentRef?: React.RefObject<HTMLDivElement>;
  preventMapInteraction?: (e: React.MouseEvent | React.TouchEvent) => void;
}

type ViewMode = 'initial' | 'search' | 'dropPin';

export const MobileAddPlaceContent: React.FC<MobileAddPlaceContentProps> = ({
  contentRef,
  preventMapInteraction,
}) => {
  const { t, i18n } = useTranslation();
  const setListMode = useSetRecoilState(atom_listModeState);
  const isDesktop = useRecoilValue(atom_isDesktop);
  const activeTour = useRecoilValue(atom_tourState);
  const [selectedPlace, setSelectedPlace] = useRecoilState(atom_selectedPlace);
  const [selectedUserPlace, setSelectedUserPlace] = useRecoilState(atom_selectedUserPlace);
  const [dropPinMode, setDropPinMode] = useRecoilState(atom_dropPinMode);
  const [userAddedPlaces, setUserAddedPlaces] = useRecoilState(atom_userAddedPlaces);
  const [allBookmarks, setAllBookmarks] = useRecoilState(atom_allBookmarksState);
  const setNavState = useSetRecoilState(atom_navigationHelperState);
  const setActiveStop = useSetRecoilState(atom_activeStopState);
  const map = useRecoilValue(atom_mapRef);

  // Get user added stops for current tour
  const tourId = activeTour?.id;
  const userStops = tourId ? (userAddedPlaces[tourId] || []) : [];

  // Track online/offline status
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // View mode state - start with dropPin if coming from main map positioning
  const [viewMode, setViewMode] = useState<ViewMode>(
    dropPinMode.step === 'naming' && dropPinMode.pinLocation ? 'dropPin' : 'initial'
  );

  // Ref to measure content height
  const contentBoxRef = useRef<HTMLDivElement>(null);

  // Min constraint
  const minHeight = 200;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
  const maxHeight = Math.floor(viewportHeight * 0.8);
  const bottomBarHeight = 60;

  const { currentHeight, setCurrentHeight, isDragging, dragHandleProps, containerTransitionStyle, startExit } = useResizablePanel({
    minHeight,
    maxHeight,
    initialHeight: 300,
  });

  // Detect RTL language (Hebrew) - use document.dir set globally by TourSummaryOverlay
  const isRTL = document.dir === 'rtl' || i18n.language === 'he';


  // Detect when opened with a selected user place - always route to dropPin form
  useEffect(() => {
    if (selectedUserPlace) {
      setViewMode('dropPin');
    }
  }, [selectedUserPlace]);

  // Detect when coming from main map positioning (dropPinMode.step === 'naming')
  useEffect(() => {
    if (dropPinMode.step === 'naming' && dropPinMode.pinLocation) {
      setViewMode('dropPin');
    }
  }, [dropPinMode.step, dropPinMode.pinLocation]);

  // Get default list mode based on device and config
  const getDefaultListMode = () => {
    if (isDesktop) return 'desktop';
    if (activeTour?.config?.['show_list']) return 'mobileList';
    return 'mobileSwiper';
  };

  // Close and return to default list mode with exit animation
  const handleClose = () => {
    startExit(() => {
      // Remove temp place if user didn't finish saving (only for new temp places, not existing)
      const tempId = dropPinMode.tempPlaceId;
      const tourId = activeTour?.id || '';
      const existingPlace = tempId && tourId ? (userAddedPlaces[tourId] || []).find((p: any) => p.place_id === tempId) : null;
      const isEditingExisting = existingPlace && (existingPlace as any).updatedAt;
      if (!isEditingExisting && tempId && activeTour?.id) {
        const tourId = activeTour.id;
        const currentPlaces = userAddedPlaces[tourId] || [];
        const filtered = currentPlaces.filter((p: any) => p.place_id !== tempId);
        const updatedPlaces = { ...userAddedPlaces, [tourId]: filtered };
        setUserAddedPlaces(updatedPlaces);
        localStorage.setItem('userAddedPlaces', JSON.stringify(updatedPlaces));
      }
      setDropPinMode({ isActive: false, step: null, pinLocation: null });
      setSelectedPlace(null);
      setSelectedUserPlace(null);
      setViewMode('initial');
      setListMode(getDefaultListMode());
    });
  };

  // Prevent map interaction handler
  const handlePreventMapInteraction = preventMapInteraction || ((e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
  });

  // Handle Search for a Place click
  const handleSearchPlace = () => {
    setViewMode('search');
  };

  // Handle Drop a Pin click - activate positioning mode on main map
  const handleDropPin = () => {
    // Close the bottom sheet
    setListMode(null);
    // Activate drop pin positioning mode on main map
    setDropPinMode({
      isActive: true,
      step: 'positioning',
      pinLocation: null,
    });
  };

  // Handle back from child component - go back to initial view
  const handleChildClose = () => {
    setViewMode('initial');
    setSelectedUserPlace(null);
    setDropPinMode({ isActive: false, step: null, pinLocation: null });
  };

  // Option row component for initial view
  const OptionRow = ({
    icon,
    title,
    onClick,
    disabled,
  }: {
    icon: React.ReactNode;
    title: string;
    onClick: () => void;
    id?: string;
    disabled?: boolean;
  }) => (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        px: 1,
        py: 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        bgcolor: disabled ? '#E0E0E0' : '#F6F6F9',
        borderRadius: '16px',
        height: '100%',
        border: disabled ? '0.5px solid #9E9E9E' : '0.5px solid #48526B',
        boxShadow: disabled ? 'none' : '0px 2px 4px rgba(72, 82, 107, 0.2)',
        opacity: disabled ? 0.6 : 1,
        '&:active': disabled ? {} : {
          bgcolor: '#E8E8EB',
          transform: 'scale(0.95)',
        },
      }}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: 'white',
          border: '1px solid #E0E0E0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 600,
          color: disabled ? '#9E9E9E' : '#333',
          lineHeight: 1.3,
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  // Get current tour's bookmarks
  const currentTourBookmarks = tourId ? (allBookmarks[tourId] || []) : [];

  // Check if a place is saved/bookmarked
  const isPlaceSaved = (placeId: string) => {
    return currentTourBookmarks.includes(placeId);
  };

  // Toggle bookmark for a user stop
  const handleToggleBookmark = (place: PlaceDetails) => {
    if (!tourId) return;

    if (isPlaceSaved(place.place_id)) {
      // Remove from bookmarks
      const updatedBookmarks = currentTourBookmarks.filter(id => id !== place.place_id);
      setAllBookmarks({
        ...allBookmarks,
        [tourId]: updatedBookmarks,
      });
    } else {
      // Add to bookmarks
      setAllBookmarks({
        ...allBookmarks,
        [tourId]: [...currentTourBookmarks, place.place_id],
      });
    }
  };

  // Handle directions for a user stop
  const handleDirections = (place: PlaceDetails) => {
    // Create a minimal stop object for navigation
    const stopForNav = {
      stop_location: {
        latitude: place.geometry.location.lat,
        longitude: place.geometry.location.lng,
      },
      s_title: place.name,
      location: {
        latitude: place.geometry.location.lat,
        longitude: place.geometry.location.lng,
      },
    };
    setActiveStop(stopForNav as any);
    setListMode('mobileNavigation');
  };

  // Handle show more for a user stop - always route to dropPin form
  const handleShowMore = (place: PlaceDetails) => {
    setSelectedUserPlace(place);
    setViewMode('dropPin');
    if (map) {
      map.flyTo([place.geometry.location.lat, place.geometry.location.lng], 17, { duration: 1 });
    }
  };

  // Handle card click - fly to location
  const handleCardClick = (place: PlaceDetails) => {
    if (map) {
      map.flyTo([place.geometry.location.lat, place.geometry.location.lng], 17, { duration: 1 });
    }
  };

  // User Stop Card component
  const UserStopCard = ({ place }: { place: PlaceDetails }) => {
    return (
      <Box
        onClick={() => handleCardClick(place)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mb: 1.5,
          cursor: 'pointer',
        }}
      >
        {/* Card Body */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'white',
            borderRadius: '8px',
            boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}
        >
          {/* Card Content */}
          <Box sx={{ p: 2 }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#333',
                mb: 0.5,
              }}
            >
              {place.name}
            </Typography>
            {place.description && (
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#666',
                  mb: 0.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {place.description}
              </Typography>
            )}
            {place.formatted_address && (
              <Typography
                sx={{
                  fontSize: '12px',
                  color: '#999',
                }}
              >
                {place.formatted_address}
              </Typography>
            )}
          </Box>

          <Divider />

          {/* Card Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              py: 1,
            }}
          >
            <Box
              onClick={(e) => { e.stopPropagation(); handleShowMore(place); }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#333',
                }}
              >
                {t('new_app.explore_more') || 'Show more'}
              </Typography>
              <i
                className={isRTL ? "fa-solid fa-angle-left" : "fa-solid fa-angle-right"}
                style={{ fontSize: '14px', color: '#333' }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleDirections(place); }}
                sx={{ p: 0.5 }}
              >
                <img src={DirectionIcon} alt="" aria-hidden="true" style={{ width: '21px', height: '21px' }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  // Render initial view (two buttons)
  const renderInitialView = () => (
    <>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: 'row',
        px: 2,
        pt: 1,
        pb: 1,
        gap: 1,
      }}>
        {/* "Add Stop" button */}
        <Button
          variant="text"
          onClick={handleDropPin}
          sx={{
            textTransform: 'none',
            color: '#48526B',
            fontSize: '16px',
            lineHeight: '20px',
            fontWeight: 600,
            justifyContent: 'center',
            '&:hover': { bgcolor: 'transparent' },
          }}
        >
          <i className="fa-solid fa-plus" style={{ marginInlineEnd: 8 }} />
          {t('drop_a_pin') || 'Add Stop'}
        </Button>
        {/* Back button */}
        <Button
          onClick={handleClose}
          variant="text"
          disableRipple
          sx={{
            backgroundColor: 'transparent',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            minWidth: 'auto',
            p: 0,
            color: '#48526B',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
          }}
        >
          <i className="fa-solid fa-xmark" style={{ fontSize: '18px' }} />
        </Button>
      </Box>
      {/* <Divider sx={{ color: '#E3E3E3', marginBottom: '10px' }} /> */}
      <Box
        ref={contentRef}
        sx={{
          overflowY: 'auto',
          flex: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          px: 0,
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
        }}
      >


        <Divider sx={{ color: '#E3E3E3', marginBottom: '10px' }} />

        {/* User Added Stops List */}
        {userStops.length > 0 && (
          <Box sx={{ px: 2, pb: 2 }}>
            {userStops.map((place) => (
              <UserStopCard key={place.place_id} place={place} />
            ))}
          </Box>
        )}
      </Box>
    </>
  );

  // If in dropPin mode, render the MobileDropAPin component
  if (viewMode === 'dropPin') {
    return (
      <MobileDropAPin
        onClose={handleChildClose}
        isRTL={isRTL}
        bottomBarHeight={bottomBarHeight}
      />
    );
  }

  // Content box with integrated drag handle (initial view)
  return (
    <>
      <Box
        ref={contentBoxRef}
        dir={isRTL ? 'rtl' : 'ltr'}
        sx={{
          boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.15)',
          bgcolor: '#F6F6F6',
          alignSelf: 'center',
          width: '100%',
          maxWidth: '530px',
          height: `${currentHeight}px`,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          textAlign: isRTL ? 'right' : 'left',
          position: 'fixed',
          zIndex: Z_INDEX.modal,
          borderRadius: '20px 20px 0px 0px',
          bottom: bottomBarHeight,
          left: 0,
          right: 0,
          px: { xs: 1.5, md: 3 },
          touchAction: 'none',
          pointerEvents: 'auto',
          isolation: 'isolate',
          ...containerTransitionStyle,
        }}
        onClick={handlePreventMapInteraction}
        onMouseDown={handlePreventMapInteraction}
        onMouseMove={handlePreventMapInteraction}
      >
        {/* Drag Handle */}
        {!isDesktop && (
          <Box
            {...dragHandleProps}
            sx={{
              position: 'relative',
              zIndex: 999999,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'ns-resize',
              touchAction: 'none',
              padding: '12px 0',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px 40px',
              }}
            >
              <Box
                sx={{
                  bgcolor: 'rgba(95,93,92,1)',
                  height: '2px',
                  width: '60px',
                  borderRadius: '8px',
                  mb: 0.5,
                }}
              />
              <Box
                sx={{
                  bgcolor: 'rgba(95,93,92,1)',
                  height: '2px',
                  width: '60px',
                  borderRadius: '8px',
                }}
              />
            </Box>
          </Box>
        )}
        {renderInitialView()}
      </Box>
    </>
  );
};

export default MobileAddPlaceContent;
